import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationNotEnabled from "../shared/IntegrationNotEnabled";
import ToastDataMapping from "./ToastDataMapping";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import {
  SectionCard,
  FormRow,
  FieldInput,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  Modal,
  ModalShell,
  Divider,
  Tile,
} from "@/components/ds";
import {
  Lightbulb,
  ArrowLeft,
  ChevronDown,
  Check,
  AlertCircle,
} from "@/components/ds/icons";

const TOAST_COLOR = "#FF4500";

// ─── Exported nav helpers ──────────────────────────────────────────────────────

export function BackToIntegrationLandingToast() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="flex cursor-pointer items-center gap-2 text-zinc-600"
      onClick={() => navigate("/integrations")}
    >
      <ArrowLeft className="w-4 h-4" />
      Back to integrations
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ToastConfig {
  id: number;
  siteId: number;
  displayName: string;
  locationName: string;
  restaurantGuid: string;
  enabled: boolean;
  createdAt: string;
}

type TabId = "overview" | "accounts" | "ai-tools" | "data-mapping" | "logs";

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  { title: "Menu intelligence",      description: "Answer 'do you have X?', dietary restrictions, prices, and today's specials in real time" },
  { title: "Order status",           description: "Customers ask 'is my order ready?' and get live status without calling the restaurant" },
  { title: "Table availability",     description: "Check floor plan and table status for walk-in and reservation conversations" },
  { title: "Restaurant information", description: "Hours, location, parking, dining options, and contact info always on hand" },
  { title: "Sales reporting",        description: "Daily revenue, top items, and check averages for management conversations" },
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  { name: "toast_get_menu_groups", label: "Get menu groups", group: "Menu", description: "Retrieve all menu categories/groups from Toast (e.g., Appetizers, Mains, Desserts) with item counts." },
  { name: "toast_search_menu", label: "Search menu", group: "Menu", description: "Search menu items by name, keyword, or dietary term. Returns matching items with price and description." },
  { name: "toast_get_menu_item", label: "Get menu item", group: "Menu", description: "Get details for a specific menu item by name — price, description, calories, and image." },
  { name: "toast_get_allergen_info", label: "Get allergen info", group: "Menu", description: "Get allergen and ingredient information for a menu item. Always advise confirming with staff." },
  { name: "toast_get_specials", label: "Get specials", group: "Menu", description: "Get current daily specials, featured items, and seasonal offerings from Toast." },
  { name: "toast_get_modifiers", label: "Get item modifiers", group: "Menu", description: "Get customization options for a menu item — sizes, add-ons, toppings, and any additional pricing." },
  { name: "toast_get_items_by_tag", label: "Get items by dietary tag", group: "Menu", description: "Find items matching a dietary preference — vegan, gluten-free, vegetarian, dairy-free, etc." },
  { name: "toast_get_order", label: "Get order", group: "Orders", description: "Get full details for an order by ID — items, quantities, status, and timestamps." },
  { name: "toast_get_table_order", label: "Get table order", group: "Orders", description: "Look up the active order for a specific table number." },
  { name: "toast_get_restaurant_info", label: "Get restaurant info", group: "Restaurant info", description: "General restaurant info — name, address, phone, and contact details." },
  { name: "toast_get_hours", label: "Get hours", group: "Restaurant info", description: "Current operating hours for all days of the week from Toast." },
  { name: "toast_get_restaurant_services", label: "Get services", group: "Restaurant info", description: "Check which ordering modes are enabled — dine-in, takeout, delivery, online ordering." },
  { name: "toast_get_discounts", label: "Get discounts", group: "Restaurant info", description: "Get active discounts and promotions configured in Toast." },
  { name: "toast_get_shift_info", label: "Get shift info", group: "Restaurant info", description: "Get current shift info — employees clocked in. Requires Toast labor API permissions." },
];

// ─── Create Form (modal) ──────────────────────────────────────────────────────

interface CreateFormProps {
  onSuccess(): void;
  onCancel(): void;
}

function CreateForm({ onSuccess, onCancel }: CreateFormProps) {
  const api = useApi();
  const [form, setForm] = useState({ displayName: "", clientId: "", clientSecret: "", restaurantGuid: "", locationName: "" });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; locationName?: string; error?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleTest() {
    if (!form.clientId || !form.clientSecret || !form.restaurantGuid) {
      setError("Client ID, Client secret, and Restaurant GUID are required.");
      return;
    }
    setError(null);
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await api.admin.post("Toast/Test", {
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim(),
        restaurantGuid: form.restaurantGuid.trim(),
      });
      const data = await resp.json();
      setTestResult(data);
      if (data.valid && data.locationName) {
        setForm((f) => ({
          ...f,
          locationName: data.locationName,
          displayName: f.displayName || data.locationName,
        }));
      }
    } catch (err) {
      console.error("Toast test failed:", err);
      setTestResult({ valid: false, error: "Connection test failed." });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    if (!form.clientId || !form.clientSecret || !form.restaurantGuid) {
      setError("All required fields must be filled.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const resp = await api.admin.post("Toast/Configs", { ...form });
      if (!resp.ok) {
        const d = await resp.json();
        setError(d?.error ?? "Save failed.");
        return;
      }
      onSuccess();
    } catch (err) {
      console.error("Toast save failed:", err);
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open size="md" onClose={onCancel} closeOnEsc={false} closeOnBackdropClick={false}>
        <ModalShell.Header
          title="Connect Toast POS"
          description="Find these in Toast Web → Integrations → Toast API. Create credentials to get your Client ID and Secret. Your Restaurant GUID is in Toast Web → Restaurant Info."
          onClose={onCancel}
        />
        <ModalShell.Body>
          {error && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}
          <div className="space-y-3">
            <FormRow label="Display name">
              <FieldInput
                placeholder="e.g. Downtown location"
                value={form.displayName}
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              />
            </FormRow>
            <FormRow label="Client ID" hint="Required">
              <FieldInput
                placeholder="Toast Client ID"
                value={form.clientId}
                onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
              />
            </FormRow>
            <FormRow label="Client secret" hint="Required">
              <FieldInput
                type="password"
                placeholder="Toast Client Secret"
                value={form.clientSecret}
                onChange={(e) => setForm((f) => ({ ...f, clientSecret: e.target.value }))}
              />
            </FormRow>
            <FormRow label="Restaurant GUID" hint="Required">
              <FieldInput
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={form.restaurantGuid}
                onChange={(e) => setForm((f) => ({ ...f, restaurantGuid: e.target.value }))}
              />
            </FormRow>
          </div>
          {testResult && (
            <div
              className={`mt-3 rounded-md px-3 py-2 text-xs flex items-center gap-2 ${
                testResult.valid
                  ? "bg-green-50 border border-green-200 text-green-700"
                  : "bg-red-50 border border-red-200 text-red-700"
              }`}
            >
              {testResult.valid ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              <span>
                {testResult.valid
                  ? `Connected — ${testResult.locationName || "Restaurant verified"}`
                  : testResult.error ?? "Connection failed."}
              </span>
            </div>
          )}
        </ModalShell.Body>
        <ModalShell.Footer>
          <SecondaryButton onClick={handleTest} disabled={testing}>
            {testing ? "Testing…" : "Test connection"}
          </SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save connection"}
          </PrimaryButton>
        </ModalShell.Footer>
    </Modal>
  );
}

// ─── Capabilities (collapsible) ───────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <SectionCard
      title="What's included"
      description={`${CAPABILITIES.length} feature areas — click to expand`}
      action={
        <GhostButton onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <span className="inline-flex items-center gap-1">
            {open ? "Hide" : "Show"}
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </span>
        </GhostButton>
      }
    >
      {open && (
        <>
          <div className="rounded-md bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>AI tools work without any configuration.</strong> Connect Toast (API credentials) and the AI
            gains access to menus, orders, and restaurant data in real time.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CAPABILITIES.map((cap) => (
              <Tile key={cap.title}>
                <Tile.Body>
                  <Tile.Title>{cap.title}</Tile.Title>
                  <Tile.Description>{cap.description}</Tile.Description>
                </Tile.Body>
              </Tile>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ─── How it works ─────────────────────────────────────────────────────────────

function HowItWorksCard() {
  return (
    <SectionCard
      title="How Toast works in Velaro"
      description="Connect once — every menu item, order, and restaurant detail becomes AI-searchable instantly."
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Tile>
          <Tile.Body>
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: TOAST_COLOR }}
              >
                1
              </span>
              <Tile.Title>Connect Toast</Tile.Title>
            </div>
            <Tile.Description>
              In Toast Web → Integrations → Toast API → create API credentials → copy Client ID and Secret → find
              Restaurant GUID in restaurant info.
            </Tile.Description>
          </Tile.Body>
        </Tile>
        <Tile>
          <Tile.Body>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                2
              </span>
              <Tile.Title>14 AI tools activate</Tile.Title>
            </div>
            <Tile.Description>
              Every menu, order, and restaurant detail is instantly searchable — no extra setup.
            </Tile.Description>
          </Tile.Body>
        </Tile>
        <Tile>
          <Tile.Body>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 bg-[var(--color-primary)]">
                3
              </span>
              <Tile.Title>Scale 24/7 guest support</Tile.Title>
            </div>
            <Tile.Description>
              Answer menu questions, order status, and hours instantly — without tying up staff.
            </Tile.Description>
          </Tile.Body>
        </Tile>
      </div>
    </SectionCard>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

function OverviewTab({ hasConnection, onConnect, onOpenWizard }: { hasConnection: boolean; onConnect(): void; onOpenWizard(): void }) {
  return (
    <div className="space-y-4">
      <HowItWorksCard />
      {!hasConnection ? (
        <SectionCard
          title="Connect your Toast account"
          description="Enter your API credentials and your menus, orders, and restaurant data become instantly searchable by the AI during any conversation."
          action={
            <button
              type="button"
              onClick={onConnect}
              className="shrink-0 px-4 py-2 rounded-md text-sm font-medium text-white"
              style={{ backgroundColor: TOAST_COLOR }}
            >
              Connect now
            </button>
          }
        />
      ) : (
        <SectionCard
          title="Launch starter workflow wizard"
          description="Build a Menu Q&A Bot, Order Status Bot, or Restaurant Concierge in minutes using a pre-built Toast template."
          action={<PrimaryButton onClick={onOpenWizard}>Launch wizard</PrimaryButton>}
        />
      )}
    </div>
  );
}

function AiToolsTab({ hasConnection, onOpenWizard }: { hasConnection: boolean; onOpenWizard(): void }) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <SectionCard
      title="AI tools"
      description="All tools activate automatically on connect — no configuration required."
      action={hasConnection ? <LaunchWizardButton onClick={onOpenWizard} /> : undefined}
    >
      <div className="flex items-center gap-2 mb-4">
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            hasConnection ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"
          }`}
        >
          {hasConnection ? `${AI_TOOLS.length} active` : "No connection"}
        </span>
      </div>
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">{group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <Tile key={tool.name} density="compact">
                  <Tile.Leading>
                    <div
                      className="w-2 h-2 rounded-full mt-1.5"
                      style={{ backgroundColor: TOAST_COLOR }}
                    />
                  </Tile.Leading>
                  <Tile.Body>
                    <Tile.Title>{tool.label}</Tile.Title>
                    <div className="text-[10px] font-mono text-zinc-400 mb-1">{tool.name}</div>
                    <Tile.Description>{tool.description}</Tile.Description>
                  </Tile.Body>
                </Tile>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function AccountsTab({ configs, onRefresh, onAdd }: { configs: ToastConfig[]; onRefresh(): void; onAdd(): void }) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);
  const [deletingConfirm, setDeletingConfirm] = useState<number | null>(null);

  async function handleDelete(id: number) {
    setDeleting(id);
    try {
      await api.admin.delete(`Toast/Configs/${id}`);
      onRefresh();
    } catch (err) {
      console.error("Toast: failed to delete config:", err);
    } finally {
      setDeleting(null);
      setDeletingConfirm(null);
    }
  }

  return (
    <SectionCard
      title="Connected accounts"
      action={
        <button
          type="button"
          onClick={onAdd}
          className="px-3 py-1.5 rounded-md text-xs font-medium text-white"
          style={{ backgroundColor: TOAST_COLOR }}
        >
          + Add account
        </button>
      }
    >
      {configs.length === 0 ? (
        <div className="text-center py-12">
          <div className="font-medium text-zinc-700 mb-1">No Toast account connected</div>
          <p className="text-sm text-zinc-500 mb-4">
            Connect your Toast API credentials to enable AI-powered restaurant support for your guests.
          </p>
          <button
            type="button"
            onClick={onAdd}
            className="px-4 py-2 rounded-md text-sm font-medium text-white"
            style={{ backgroundColor: TOAST_COLOR }}
          >
            Connect Toast
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((c) => (
            <Tile key={c.id}>
              <Tile.Leading>
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center text-white text-lg font-bold"
                  style={{ backgroundColor: TOAST_COLOR }}
                >
                  T
                </div>
              </Tile.Leading>
              <Tile.Body>
                <Tile.Title>{c.displayName}</Tile.Title>
                {c.locationName && <Tile.Description>{c.locationName}</Tile.Description>}
                <div className="text-xs text-zinc-400 font-mono">GUID: ****{c.restaurantGuid?.slice(-8)}</div>
                <div className="text-xs text-zinc-400">{new Date(c.createdAt).toLocaleDateString()}</div>
              </Tile.Body>
              <Tile.Trailing>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    c.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"
                  }`}
                >
                  {c.enabled ? "Active" : "Disabled"}
                </span>
                {deletingConfirm === c.id ? (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-zinc-600">Remove?</span>
                    <GhostButton onClick={() => handleDelete(c.id)} disabled={deleting === c.id}>
                      <span className="text-red-600">{deleting === c.id ? "Removing…" : "Yes"}</span>
                    </GhostButton>
                    <GhostButton onClick={() => setDeletingConfirm(null)}>Cancel</GhostButton>
                  </div>
                ) : (
                  <GhostButton onClick={() => setDeletingConfirm(c.id)}>
                    <span className="text-red-500">Remove</span>
                  </GhostButton>
                )}
              </Tile.Trailing>
            </Tile>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Toast() {
  const api = useApi();
  const [configs, setConfigs] = useState<ToastConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const { teamId } = useSelectedTeam();

  const fetchConfigs = useCallback(async () => {
    setLoadError(null);
    try {
      const resp = await api.admin.get("Toast/Configs");
      if (resp.status === 402) {
        setEnabled(false);
        return;
      }
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch (err) {
      setLoadError(`Failed to load configs: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    if (configs.length > 0 && selectedConfigId === 0) {
      setSelectedConfigId(configs.find((c) => c.enabled)?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  if (!enabled) {
    return (
      <IntegrationNotEnabled
        name="Toast Integration"
        description="Toast integration requires an upgrade. Contact Velaro support to enable it for your account."
      />
    );
  }

  const hasActiveConfig = configs.some((c) => c.enabled);

  const tabsJSX = (
    <>
      <IntegrationTab active={activeTab === "overview"} onClick={() => setActiveTab("overview")}>Overview</IntegrationTab>
      <IntegrationTab active={activeTab === "accounts"} onClick={() => setActiveTab("accounts")}>
        Accounts{configs.length > 0 && <span className="text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">{configs.length}</span>}
      </IntegrationTab>
      <IntegrationTab active={activeTab === "ai-tools"} onClick={() => setActiveTab("ai-tools")}>AI Tools</IntegrationTab>
      <IntegrationTab active={activeTab === "data-mapping"} onClick={() => setActiveTab("data-mapping")}>Field Mapping</IntegrationTab>
      <IntegrationTab active={activeTab === "logs"} onClick={() => setActiveTab("logs")}>Activity Log</IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Toast" tabs={tabsJSX}>
      {showWizard && <StarterWorkflowWizard integration="toast" onClose={() => setShowWizard(false)} />}
      {showForm && (
        <CreateForm
          onSuccess={() => {
            setShowForm(false);
            fetchConfigs();
            setActiveTab("accounts");
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loadError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 mb-4">{loadError}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="space-y-4">
          {activeTab === "overview" && (
            <>
              <CapabilitiesPanel />
              <OverviewTab
                hasConnection={hasActiveConfig}
                onConnect={() => setShowForm(true)}
                onOpenWizard={() => setShowWizard(true)}
              />
            </>
          )}
          {activeTab === "accounts" && (
            <>
              {hasActiveConfig && (
                <SectionCard
                  title="Deploy the chat widget to your site"
                  description="Your Toast integration is connected — grab your embed code from Deployments to go live."
                  action={
                    <div className="flex items-center gap-2 shrink-0">
                      <Link
                        to="/chat_window"
                        className="text-xs px-3 py-1.5 rounded-md border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition"
                      >
                        Chat window
                      </Link>
                      <Link
                        to="/deployment"
                        className="text-xs px-3 py-1.5 rounded-md text-white transition bg-[var(--color-primary)] hover:opacity-90"
                      >
                        Get embed code
                      </Link>
                    </div>
                  }
                >
                  <div className="flex items-start gap-2 rounded-md px-3 py-2 text-xs text-zinc-700 bg-zinc-50 border border-zinc-200">
                    <Lightbulb className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                    <span>
                      <strong>Tip:</strong> Use <strong>Page widgets</strong> from{" "}
                      <Link to="/deployment" className="underline text-[var(--color-primary)]">
                        Deployments → Page widgets
                      </Link>{" "}
                      to add a lightweight menu Q&A widget on your ordering page — no live agent required.
                    </span>
                  </div>
                </SectionCard>
              )}
              <AccountsTab configs={configs} onRefresh={fetchConfigs} onAdd={() => setShowForm(true)} />
            </>
          )}
          {activeTab === "ai-tools" && (
            <AiToolsTab hasConnection={hasActiveConfig} onOpenWizard={() => setShowWizard(true)} />
          )}
          {activeTab === "data-mapping" && (
            <SectionCard title="Field Mapping — Toast">
              <ToastDataMapping teamId={teamId ?? 0} selectedConfigId={selectedConfigId} />
            </SectionCard>
          )}
          {activeTab === "logs" && (
            <IntegrationActivityLog integrationSlug="Toast" />
          )}
        </div>
      )}
    </StickyRouteHeader>
  );
}
